import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/properties", label: "Properties" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-gold/20"
                    : "bg-[#0A0A0A]/70 backdrop-blur-md"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3"
                    aria-label="Leo's Luxury home"
                >
                    <img
                        src="/brand/leos-luxury-logo.png"
                        alt="Leo's Luxury"
                        className="h-14 w-auto"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[12px] uppercase tracking-[0.22em] font-medium link-underline transition-colors ${
                                    isActive ? "text-gold" : "text-cream/90 hover:text-gold"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <a
                        href="tel:0469076071"
                        data-testid="nav-phone-link"
                        className="text-[12px] uppercase tracking-[0.22em] text-cream/70 hover:text-gold transition-colors"
                    >
                        0469 076 071
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className="btn-gold"
                    >
                        Book Consultation
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    className="md:hidden text-cream p-2"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-gold/20"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                className="text-sm uppercase tracking-[0.22em] text-cream hover:text-gold"
                                end={l.to === "/"}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-nav-cta"
                            className="btn-gold text-center mt-2"
                        >
                            Book Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
